import { createContext, useContext } from 'react';

/**
 * Public-link credentials provided to embedded `ContentPick` / `ViewContentQuery`
 * components so they can call `publicLinksService.resolveComponent`.
 *
 * Two shapes:
 *
 * - **Top-level** (`PublicLinkAccess` page): the visitor opened the share directly.
 *   We have the user-facing `token` and (after password clearance) the `password`.
 *
 * - **Nested** (`PublicContentAccess` rendering a view-target tile): the visitor
 *   is already past the top-level password gate. The signed `accessToken` they
 *   used to arrive here is proof of clearance and is sent in place of `token`
 *   / `password` on resolveComponent calls. The server uses the token's
 *   `contentId` claim as the source view whose pageDoc is walked.
 *
 * Components branch on which field is present.
 */
export type PublicLinkCredentials =
  | {
      linkId: string;
      token: string;
      password?: string;
      /**
       * Share-session token (proof of a prior password gate clearance).
       * Either this OR the password is sufficient. Components forward
       * whichever they have to resolveComponent.
       */
      shareToken?: string;
      accessToken?: undefined;
    }
  | {
      linkId: string;
      accessToken: string;
      token?: undefined;
      password?: undefined;
      shareToken?: undefined;
    };

export const PublicLinkAccessContext = createContext<PublicLinkCredentials | null>(null);

export function usePublicLinkAccess(): PublicLinkCredentials | null {
  return useContext(PublicLinkAccessContext);
}
